import React, { useState, useEffect, useMemo } from 'react';
import { useAppContext } from '../context/AppContext';
import { getAppointmentsByBarbershopId } from '../services/appointmentService';
import { getReviewsByBarbershop } from '../services/reviewService';
import { getPaymentsByBarbershop, computePaymentMetrics, groupPaymentsByMonth } from '../services/paymentService';
import toast from 'react-hot-toast';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';

// Registrar componentes do Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

/**
 * Componente: AnalyticsPage
 * Dashboard de analytics para donos de barbearia
 */
const AnalyticsPage = () => {
  const { currentUser } = useAppContext();
  const [appointments, setAppointments] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month'); // month, year, all
  const [metrics, setMetrics] = useState({
    totalAppointments: 0,
    completedAppointments: 0,
    canceledAppointments: 0,
    noShowAppointments: 0,
    revenue: 0,
    averageRating: 0,
    totalReviews: 0
  });

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  // Pagamentos filtrados por período
  const filteredPayments = useMemo(() => {
    const now = new Date();
    let startDate;
    if (period === 'month') startDate = new Date(now.getFullYear(), now.getMonth(), 1);
    else if (period === 'year') startDate = new Date(now.getFullYear(), 0, 1);
    else return payments;
    return payments.filter(p => p.paymentDate && new Date(p.paymentDate) >= startDate);
  }, [payments, period]);

  // Métricas de pagamento calculadas
  const paymentMetrics = useMemo(() => computePaymentMetrics(filteredPayments), [filteredPayments]);

  // Agrupamento mensal de pagamentos (6 meses)
  const paymentsByMonth = useMemo(() => groupPaymentsByMonth(filteredPayments, 6), [filteredPayments]);

  useEffect(() => {
    if (appointments.length > 0 || payments.length > 0) {
      calculateMetrics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointments, payments, period]);

  const loadData = async () => {
    // Obter barbershopId (suporta formato antigo e novo)
    const barbershopId = currentUser?.barbershopId || (currentUser?.barbershops && currentUser.barbershops[0]);
    
    if (!barbershopId) {
      toast.error('Você não está associado a uma barbearia');
      return;
    }

    setLoading(true);
    try {
      const [appointmentsResult, reviewsResult, paymentsResult] = await Promise.all([
        getAppointmentsByBarbershopId(barbershopId),
        getReviewsByBarbershop(barbershopId),
        getPaymentsByBarbershop(barbershopId),
      ]);

      if (appointmentsResult.success) setAppointments(appointmentsResult.data);
      if (reviewsResult.success) setReviews(reviewsResult.data);
      if (paymentsResult.success) setPayments(paymentsResult.data);
    } catch (error) {
      console.error('Error loading analytics data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const getFilteredAppointments = () => {
    const now = new Date();
    let startDate;

    if (period === 'month') {
      startDate = new Date(now.getFullYear(), now.getMonth(), 1);
    } else if (period === 'year') {
      startDate = new Date(now.getFullYear(), 0, 1);
    } else {
      return appointments;
    }

    return appointments.filter(apt => {
      const [y, m, d] = apt.date.split('-').map(Number);
      return new Date(y, m - 1, d) >= startDate;
    });
  };

  const calculateMetrics = () => {
    const filtered = getFilteredAppointments();
    
    const completed = filtered.filter(apt => apt.status === 'completed');
    const canceled = filtered.filter(apt => apt.status === 'canceled');
    const noShow = filtered.filter(apt => apt.status === 'no-show');
    
    // Receita real a partir dos pagamentos registrados (filtrados por período)
    const revenue = filteredPayments.reduce((sum, p) => {
      const effective =
        p.paymentMethod === 'card' && p.cardHasInterest && p.cardFinalAmount != null
          ? p.cardFinalAmount
          : p.amountCharged;
      return sum + (effective || 0);
    }, 0);

    // Calcular média de avaliações
    const avgRating = reviews.length > 0
      ? reviews.reduce((sum, rev) => sum + rev.rating, 0) / reviews.length
      : 0;

    setMetrics({
      totalAppointments: filtered.length,
      completedAppointments: completed.length,
      canceledAppointments: canceled.length,
      noShowAppointments: noShow.length,
      revenue,
      averageRating: avgRating,
      totalReviews: reviews.length
    });
  };

  const getAppointmentsByMonth = () => {
    const filtered = getFilteredAppointments();
    const monthData = {};

    filtered.forEach(apt => {
      const [y, m] = apt.date.split('-').map(Number);
      const monthKey = `${y}-${String(m).padStart(2, '0')}`;
      
      if (!monthData[monthKey]) {
        monthData[monthKey] = { completed: 0, canceled: 0, noShow: 0 };
      }

      if (apt.status === 'completed') monthData[monthKey].completed++;
      else if (apt.status === 'canceled') monthData[monthKey].canceled++;
      else if (apt.status === 'no-show') monthData[monthKey].noShow++;
    });

    const sortedKeys = Object.keys(monthData).sort();
    
    return {
      labels: sortedKeys.map(key => {
        const [year, month] = key.split('-');
        return `${month}/${year}`;
      }),
      datasets: [
        {
          label: 'Completados',
          data: sortedKeys.map(key => monthData[key].completed),
          backgroundColor: 'rgba(34, 197, 94, 0.7)',
          borderColor: 'rgb(34, 197, 94)',
          borderWidth: 1
        },
        {
          label: 'Cancelados',
          data: sortedKeys.map(key => monthData[key].canceled),
          backgroundColor: 'rgba(239, 68, 68, 0.7)',
          borderColor: 'rgb(239, 68, 68)',
          borderWidth: 1
        },
        {
          label: 'Não Compareceu',
          data: sortedKeys.map(key => monthData[key].noShow),
          backgroundColor: 'rgba(249, 115, 22, 0.7)',
          borderColor: 'rgb(249, 115, 22)',
          borderWidth: 1
        }
      ]
    };
  };

  const getRevenueByMonth = () => {
    // Usar pagamentos reais agrupados por mês
    const sortedKeys = Object.keys(paymentsByMonth).sort();

    return {
      labels: sortedKeys.map(key => {
        const [year, month] = key.split('-');
        return `${month}/${year}`;
      }),
      datasets: [
        {
          label: 'Receita Real (R$)',
          data: sortedKeys.map(key => paymentsByMonth[key]?.total || 0),
          borderColor: 'rgb(79, 70, 229)',
          backgroundColor: 'rgba(79, 70, 229, 0.1)',
          tension: 0.4,
          fill: true
        }
      ]
    };
  };

  const getPaymentMethodChart = () => {
    const { totals } = paymentMetrics;
    return {
      labels: ['Dinheiro', 'Pix', 'Cartão'],
      datasets: [
        {
          data: [totals.cash || 0, totals.pix || 0, totals.card || 0],
          backgroundColor: [
            'rgba(34, 197, 94, 0.8)',
            'rgba(59, 130, 246, 0.8)',
            'rgba(168, 85, 247, 0.8)',
          ],
          borderColor: [
            'rgb(34, 197, 94)',
            'rgb(59, 130, 246)',
            'rgb(168, 85, 247)',
          ],
          borderWidth: 2
        }
      ]
    };
  };

  const getTopCustomers = () => {
    const filtered = getFilteredAppointments();
    const completed = filtered.filter(apt => apt.status === 'completed');
    const customerMap = {};

    completed.forEach(apt => {
      const name = apt.clientName || 'Cliente desconhecido';
      const key = apt.userId || name; // agrupa por userId se disponível, senão pelo nome
      if (!customerMap[key]) {
        customerMap[key] = { name, count: 0, lastVisit: null };
      }
      customerMap[key].count++;
      const [ay, am, ad] = apt.date.split('-').map(Number);
      const aptDate = new Date(ay, am - 1, ad);
      if (!customerMap[key].lastVisit || aptDate > customerMap[key].lastVisit) {
        customerMap[key].lastVisit = aptDate;
      }
    });

    return Object.values(customerMap)
      .sort((a, b) => b.count - a.count)
      .slice(0, 15);
  };

  const getStatusDistribution = () => {
    const filtered = getFilteredAppointments();
    
    const completed = filtered.filter(apt => apt.status === 'completed').length;
    const canceled = filtered.filter(apt => apt.status === 'canceled').length;
    const noShow = filtered.filter(apt => apt.status === 'no-show').length;
    const pending = filtered.filter(apt => apt.status === 'pending' || apt.status === 'confirmed').length;

    return {
      labels: ['Completados', 'Cancelados', 'Não Compareceu', 'Pendentes'],
      datasets: [
        {
          data: [completed, canceled, noShow, pending],
          backgroundColor: [
            'rgba(34, 197, 94, 0.8)',
            'rgba(239, 68, 68, 0.8)',
            'rgba(249, 115, 22, 0.8)',
            'rgba(59, 130, 246, 0.8)'
          ],
          borderColor: [
            'rgb(34, 197, 94)',
            'rgb(239, 68, 68)',
            'rgb(249, 115, 22)',
            'rgb(59, 130, 246)'
          ],
          borderWidth: 2
        }
      ]
    };
  };

  const exportToPDF = async () => {
    const doc = new jsPDF();
    
    // Título
    doc.setFontSize(18);
    doc.text('Relatório de Analytics', 14, 22);
    
    // Período
    doc.setFontSize(11);
    const periodLabel = period === 'month' ? 'Mês Atual' : period === 'year' ? 'Ano Atual' : 'Todos os Períodos';
    doc.text(`Período: ${periodLabel}`, 14, 30);
    doc.text(`Gerado em: ${new Date().toLocaleString('pt-BR')}`, 14, 36);
    
    // Métricas principais
    doc.setFontSize(14);
    doc.text('Métricas Principais', 14, 46);
    
    doc.setFontSize(10);
    let yPos = 54;
    doc.text(`Total de Agendamentos: ${metrics.totalAppointments}`, 14, yPos);
    yPos += 6;
    doc.text(`Completados: ${metrics.completedAppointments}`, 14, yPos);
    yPos += 6;
    doc.text(`Cancelados: ${metrics.canceledAppointments}`, 14, yPos);
    yPos += 6;
    doc.text(`Não Compareceu: ${metrics.noShowAppointments}`, 14, yPos);
    yPos += 6;
    doc.text(`Receita Total: R$ ${metrics.revenue.toFixed(2)}`, 14, yPos);
    yPos += 6;
    doc.text(`Avaliação Média: ${metrics.averageRating.toFixed(1)} ⭐ (${metrics.totalReviews} avaliações)`, 14, yPos);
    yPos += 10;
    
    // Taxa de conversão
    const conversionRate = metrics.totalAppointments > 0
      ? ((metrics.completedAppointments / metrics.totalAppointments) * 100).toFixed(1)
      : 0;
    doc.text(`Taxa de Conversão: ${conversionRate}%`, 14, yPos);
    yPos += 6;
    
    const cancelRate = metrics.totalAppointments > 0
      ? ((metrics.canceledAppointments / metrics.totalAppointments) * 100).toFixed(1)
      : 0;
    doc.text(`Taxa de Cancelamento: ${cancelRate}%`, 14, yPos);
    yPos += 10;
    
    // Tabela de agendamentos por mês
    if (period !== 'all') {
      doc.setFontSize(14);
      doc.text('Agendamentos por Mês', 14, yPos);
      yPos += 8;
      
      const monthlyData = getAppointmentsByMonth();
      const tableData = monthlyData.labels.map((label, index) => [
        label,
        monthlyData.datasets[0].data[index],
        monthlyData.datasets[1].data[index],
        monthlyData.datasets[2].data[index],
        monthlyData.datasets[0].data[index] + monthlyData.datasets[1].data[index] + monthlyData.datasets[2].data[index]
      ]);
      
      doc.autoTable({
        startY: yPos,
        head: [['Mês', 'Completados', 'Cancelados', 'Não Compareceu', 'Total']],
        body: tableData,
        styles: { fontSize: 9 },
        headStyles: { fillColor: [79, 70, 229] }
      });
      
      yPos = doc.lastAutoTable.finalY + 10;
    }
    
    // Avaliações recentes
    if (reviews.length > 0) {
      doc.setFontSize(14);
      doc.text('Avaliações Recentes', 14, yPos);
      yPos += 8;
      
      const recentReviews = reviews.slice(0, 10);
      const reviewTableData = recentReviews.map(review => [
        review.userName || 'Anônimo',
        '⭐'.repeat(review.rating),
        review.comment?.substring(0, 60) + (review.comment?.length > 60 ? '...' : '') || '—'
      ]);
      
      doc.autoTable({
        startY: yPos,
        head: [['Cliente', 'Nota', 'Comentário']],
        body: reviewTableData,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [79, 70, 229] },
        columnStyles: {
          0: { cellWidth: 40 },
          1: { cellWidth: 30 },
          2: { cellWidth: 'auto' }
        }
      });
      yPos = doc.lastAutoTable.finalY + 10;
    }

    // === RANKING DE CLIENTES ===
    const topCustomers = getTopCustomers();
    if (topCustomers.length > 0) {
      doc.setFontSize(14);
      doc.text('Ranking de Clientes', 14, yPos);
      yPos += 8;

      const customerTableData = topCustomers.map((c, i) => [
        `${i + 1}º`,
        c.name,
        c.count,
        c.lastVisit ? c.lastVisit.toLocaleDateString('pt-BR') : '—'
      ]);

      doc.autoTable({
        startY: yPos,
        head: [['Pos.', 'Cliente', 'Cortes', 'Última Visita']],
        body: customerTableData,
        styles: { fontSize: 9 },
        headStyles: { fillColor: [79, 70, 229] }
      });

      yPos = doc.lastAutoTable.finalY + 10;
    }

    // === PAGAMENTOS REGISTRADOS ===
    if (filteredPayments.length > 0) {
      doc.addPage();
      yPos = 20;

      doc.setFontSize(16);
      doc.text('Controle Financeiro - Pagamentos Registrados', 14, yPos);
      yPos += 10;

      doc.setFontSize(10);
      doc.text(`Total de pagamentos: ${paymentMetrics.totalPayments}`, 14, yPos); yPos += 6;
      doc.text(`Receita total: R$ ${paymentMetrics.grandTotal.toFixed(2)}`, 14, yPos); yPos += 6;
      doc.text(`Ticket medio: R$ ${paymentMetrics.avgPerService.toFixed(2)}`, 14, yPos); yPos += 6;
      doc.text(`Dinheiro: R$ ${(paymentMetrics.totals.cash || 0).toFixed(2)} (${paymentMetrics.counts.cash || 0} transacoes)`, 14, yPos); yPos += 6;
      doc.text(`Pix: R$ ${(paymentMetrics.totals.pix || 0).toFixed(2)} (${paymentMetrics.counts.pix || 0} transacoes)`, 14, yPos); yPos += 6;
      doc.text(`Cartao Debito: R$ ${(paymentMetrics.cardSubtotals.debit || 0).toFixed(2)}`, 14, yPos); yPos += 6;
      doc.text(`Cartao Credito: R$ ${(paymentMetrics.cardSubtotals.credit || 0).toFixed(2)}`, 14, yPos); yPos += 12;

      doc.setFontSize(13);
      doc.text('Detalhamento de Pagamentos', 14, yPos);
      yPos += 8;

      const paymentTableData = filteredPayments.map(p => {
        const methodLabel =
          p.paymentMethod === 'cash' ? 'Dinheiro' :
          p.paymentMethod === 'pix'  ? 'Pix' :
          p.cardType === 'debit'     ? 'Debito' :
          p.cardInstallments > 1     ? `Credito ${p.cardInstallments}x` : 'Credito';
        const effective =
          p.paymentMethod === 'card' && p.cardHasInterest && p.cardFinalAmount
            ? p.cardFinalAmount : p.amountCharged;
        return [
          p.paymentDate || '-',
          p.clientName || '-',
          p.clientPhone || '-',
          p.serviceType || '-',
          methodLabel,
          `R$ ${Number(p.amountCharged || 0).toFixed(2)}`,
          `R$ ${Number(effective || 0).toFixed(2)}`,
          p.scheduleNext ? `${p.nextDate} ${p.nextTime}` : '-',
          p.notes || '-',
        ];
      });

      doc.autoTable({
        startY: yPos,
        head: [['Data', 'Cliente', 'Telefone', 'Servico', 'Pagamento', 'Cobrado', 'Recebido', 'Prox. Agend.', 'Obs.']],
        body: paymentTableData,
        styles: { fontSize: 7, overflow: 'linebreak' },
        headStyles: { fillColor: [22, 163, 74] },
        columnStyles: {
          0: { cellWidth: 18 },
          1: { cellWidth: 25 },
          2: { cellWidth: 20 },
          3: { cellWidth: 22 },
          4: { cellWidth: 18 },
          5: { cellWidth: 16 },
          6: { cellWidth: 16 },
          7: { cellWidth: 22 },
          8: { cellWidth: 'auto' },
        },
        margin: { left: 5, right: 5 },
      });
    }
    
    // Salvar PDF
    const filename = `analytics_${periodLabel.toLowerCase().replace(' ', '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(filename);
    
    toast.success('PDF exportado com sucesso!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando analytics...</p>
        </div>
      </div>
    );
  }

  if (!currentUser?.barbershopId && !(currentUser?.barbershops?.length)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Você não está associado a uma barbearia</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Analytics</h1>
              <p className="mt-2 text-sm text-gray-600">
                Insights e métricas da sua barbearia
              </p>
            </div>
            
            {/* Filtro de Período e Exportar */}
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              >
                <option value="month">Mês Atual</option>
                <option value="year">Ano Atual</option>
                <option value="all">Todos os Períodos</option>
              </select>
              
              <button
                onClick={exportToPDF}
                className="w-full sm:w-auto px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium whitespace-nowrap flex items-center justify-center gap-2"
              >
                <span className="hidden xs:inline">📄</span>
                <span>Exportar PDF</span>
              </button>
            </div>
          </div>
        </div>

        {/* Cards de Métricas */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total de Agendamentos</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{metrics.totalAppointments}</p>
              </div>
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                <span className="text-2xl">📅</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Completados</p>
                <p className="text-3xl font-bold text-green-600 mt-2">{metrics.completedAppointments}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {metrics.totalAppointments > 0 
                    ? `${((metrics.completedAppointments / metrics.totalAppointments) * 100).toFixed(1)}%`
                    : '0%'}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <span className="text-2xl">✅</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Receita Total</p>
                <p className="text-3xl font-bold text-indigo-600 mt-2">
                  R$ {metrics.revenue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </p>
              </div>
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                <span className="text-2xl">💰</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Avaliação Média</p>
                <p className="text-3xl font-bold text-yellow-500 mt-2">
                  {metrics.averageRating.toFixed(1)} ⭐
                </p>
                <p className="text-xs text-gray-500 mt-1">{metrics.totalReviews} avaliações</p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <span className="text-2xl">⭐</span>
              </div>
            </div>
          </div>
        </div>

        {/* Gráficos */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Gráfico de Agendamentos por Mês */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Agendamentos por Mês
            </h3>
            <Bar
              data={getAppointmentsByMonth()}
              options={{
                responsive: true,
                maintainAspectRatio: true,
                plugins: {
                  legend: {
                    position: 'bottom'
                  }
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: {
                      stepSize: 1
                    }
                  }
                }
              }}
            />
          </div>

          {/* Gráfico de Receita */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Receita Mensal
            </h3>
            <Line
              data={getRevenueByMonth()}
              options={{
                responsive: true,
                maintainAspectRatio: true,
                plugins: {
                  legend: {
                    position: 'bottom'
                  }
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: {
                      callback: (value) => `R$ ${value}`
                    }
                  }
                }
              }}
            />
          </div>
        </div>

        {/* Distribuição de Status */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow-sm p-6 lg:col-span-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Distribuição de Status
            </h3>
            <Doughnut
              data={getStatusDistribution()}
              options={{
                responsive: true,
                maintainAspectRatio: true,
                plugins: {
                  legend: {
                    position: 'bottom'
                  }
                }
              }}
            />
          </div>

          {/* Resumo de Métricas */}
          <div className="bg-white rounded-lg shadow-sm p-6 lg:col-span-2">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Resumo de Performance
            </h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center pb-3 border-b">
                <span className="text-gray-600">Taxa de Conversão</span>
                <span className="text-lg font-semibold text-green-600">
                  {metrics.totalAppointments > 0
                    ? `${((metrics.completedAppointments / metrics.totalAppointments) * 100).toFixed(1)}%`
                    : '0%'}
                </span>
              </div>
              
              <div className="flex justify-between items-center pb-3 border-b">
                <span className="text-gray-600">Taxa de Cancelamento</span>
                <span className="text-lg font-semibold text-red-600">
                  {metrics.totalAppointments > 0
                    ? `${((metrics.canceledAppointments / metrics.totalAppointments) * 100).toFixed(1)}%`
                    : '0%'}
                </span>
              </div>
              
              <div className="flex justify-between items-center pb-3 border-b">
                <span className="text-gray-600">Taxa de Não Comparecimento</span>
                <span className="text-lg font-semibold text-orange-600">
                  {metrics.totalAppointments > 0
                    ? `${((metrics.noShowAppointments / metrics.totalAppointments) * 100).toFixed(1)}%`
                    : '0%'}
                </span>
              </div>
              
              <div className="flex justify-between items-center pb-3 border-b">
                <span className="text-gray-600">Receita Média por Atendimento</span>
                <span className="text-lg font-semibold text-indigo-600">
                  R$ {paymentMetrics.totalPayments > 0
                    ? paymentMetrics.avgPerService.toFixed(2)
                    : metrics.completedAppointments > 0
                    ? (metrics.revenue / metrics.completedAppointments).toFixed(2)
                    : '0.00'}
                </span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total de Avaliações</span>
                <span className="text-lg font-semibold text-yellow-600">
                  {metrics.totalReviews}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* === RANKING DE CLIENTES === */}
        {getTopCustomers().length > 0 && (
          <div className="mt-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">🏆 Ranking de Clientes</h2>
            <div className="bg-white rounded-lg shadow-sm p-6">
              <p className="text-sm text-gray-500 mb-4">Clientes com mais cortes completados no período selecionado</p>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase tracking-wider">
                      <th className="px-3 py-2 w-12">#</th>
                      <th className="px-3 py-2">Cliente</th>
                      <th className="px-3 py-2 text-center">Cortes</th>
                      <th className="px-3 py-2">Última Visita</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {getTopCustomers().map((customer, index) => (
                      <tr key={index} className="hover:bg-gray-50 transition-colors">
                        <td className="px-3 py-2 text-center">
                          {index === 0 ? (
                            <span className="text-xl">🥇</span>
                          ) : index === 1 ? (
                            <span className="text-xl">🥈</span>
                          ) : index === 2 ? (
                            <span className="text-xl">🥉</span>
                          ) : (
                            <span className="text-gray-500 font-medium">{index + 1}º</span>
                          )}
                        </td>
                        <td className="px-3 py-2 font-medium text-gray-900">{customer.name}</td>
                        <td className="px-3 py-2 text-center">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-indigo-100 text-indigo-800">
                            {customer.count} {customer.count === 1 ? 'corte' : 'cortes'}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-gray-600">
                          {customer.lastVisit
                            ? customer.lastVisit.toLocaleDateString('pt-BR')
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* === CONTROLE FINANCEIRO - PAGAMENTOS === */}
        <div className="mt-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">💰 Controle Financeiro</h2>

          {/* Cards de formas de pagamento */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow-sm p-5">
              <p className="text-sm text-gray-500 mb-1">Receita Real (Pagamentos)</p>
              <p className="text-2xl font-bold text-green-600">
                R$ {paymentMetrics.grandTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
              <p className="text-xs text-gray-400 mt-1">{paymentMetrics.totalPayments} registro(s)</p>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-5">
              <p className="text-sm text-gray-500 mb-1">💵 Dinheiro</p>
              <p className="text-2xl font-bold text-emerald-600">
                R$ {(paymentMetrics.totals.cash || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
              <p className="text-xs text-gray-400 mt-1">{paymentMetrics.counts.cash || 0} pagamento(s)</p>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-5">
              <p className="text-sm text-gray-500 mb-1">🔑 Pix</p>
              <p className="text-2xl font-bold text-blue-600">
                R$ {(paymentMetrics.totals.pix || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
              <p className="text-xs text-gray-400 mt-1">{paymentMetrics.counts.pix || 0} pagamento(s)</p>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-5">
              <p className="text-sm text-gray-500 mb-1">💳 Cartão</p>
              <p className="text-2xl font-bold text-purple-600">
                R$ {(paymentMetrics.totals.card || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
              <p className="text-xs text-gray-400 mt-1 leading-relaxed">
                Débito: R$ {(paymentMetrics.cardSubtotals.debit || 0).toFixed(2)}<br />
                Crédito: R$ {(paymentMetrics.cardSubtotals.credit || 0).toFixed(2)}
              </p>
            </div>
          </div>

          {/* Gráficos de pagamento lado a lado */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Forma de pagamento (doughnut) */}
            {paymentMetrics.totalPayments > 0 && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Distribuição por Forma de Pagamento</h3>
                <Doughnut
                  data={getPaymentMethodChart()}
                  options={{
                    responsive: true,
                    maintainAspectRatio: true,
                    plugins: { legend: { position: 'bottom' } }
                  }}
                />
              </div>
            )}

            {/* Ticket médio e top clientes */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Resumo Financeiro</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center pb-2 border-b">
                  <span className="text-gray-600">Ticket Médio</span>
                  <span className="font-semibold text-indigo-600">
                    R$ {paymentMetrics.avgPerService.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b">
                  <span className="text-gray-600">Forma mais usada</span>
                  <span className="font-semibold text-gray-800">
                    {(() => {
                      const c = paymentMetrics.counts;
                      const max = Math.max(c.cash || 0, c.pix || 0, c.card || 0);
                      if (max === 0) return '—';
                      if (max === c.cash) return '💵 Dinheiro';
                      if (max === c.pix) return '🔑 Pix';
                      return '💳 Cartão';
                    })()}
                  </span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b">
                  <span className="text-gray-600">Maior receita em</span>
                  <span className="font-semibold text-gray-800">
                    {(() => {
                      const t = paymentMetrics.totals;
                      const max = Math.max(t.cash || 0, t.pix || 0, t.card || 0);
                      if (max === 0) return '—';
                      if (max === t.cash) return '💵 Dinheiro';
                      if (max === t.pix) return '🔑 Pix';
                      return '💳 Cartão';
                    })()}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Total registros de pagamento</span>
                  <span className="font-semibold text-gray-800">{paymentMetrics.totalPayments}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Tabela de pagamentos recentes */}
          {filteredPayments.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Pagamentos Recentes</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase tracking-wider">
                      <th className="px-3 py-2">Data</th>
                      <th className="px-3 py-2">Cliente</th>
                      <th className="px-3 py-2">Telefone</th>
                      <th className="px-3 py-2">Serviço</th>
                      <th className="px-3 py-2">Forma</th>
                      <th className="px-3 py-2">Cobrado</th>
                      <th className="px-3 py-2">Recebido</th>
                      <th className="px-3 py-2">Próx. Agend.</th>
                      <th className="px-3 py-2">Obs.</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredPayments.slice(0, 50).map(p => {
                      const method =
                        p.paymentMethod === 'cash' ? '💵 Dinheiro' :
                        p.paymentMethod === 'pix'  ? '🔑 Pix' :
                        p.cardType === 'debit'      ? '💳 Débito' :
                        p.cardInstallments > 1      ? `💳 Crédito ${p.cardInstallments}x` : '💳 Crédito';
                      const effective =
                        p.paymentMethod === 'card' && p.cardHasInterest && p.cardFinalAmount != null
                          ? p.cardFinalAmount : p.amountCharged;
                      return (
                        <tr key={p.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-3 py-2 whitespace-nowrap text-gray-700">
                            {p.paymentDate ? new Date(p.paymentDate + 'T12:00:00').toLocaleDateString('pt-BR') : '—'}
                          </td>
                          <td className="px-3 py-2 font-medium text-gray-900">{p.clientName || '—'}</td>
                          <td className="px-3 py-2 text-gray-600">{p.clientPhone || '—'}</td>
                          <td className="px-3 py-2 text-gray-600">{p.serviceType || '—'}</td>
                          <td className="px-3 py-2 whitespace-nowrap">{method}</td>
                          <td className="px-3 py-2 text-gray-700">R$ {Number(p.amountCharged || 0).toFixed(2)}</td>
                          <td className="px-3 py-2 font-semibold text-green-700">R$ {Number(effective || 0).toFixed(2)}</td>
                          <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                            {p.scheduleNext ? `${p.nextDate} ${p.nextTime}` : '—'}
                          </td>
                          <td className="px-3 py-2 text-gray-500 max-w-[120px] truncate" title={p.notes}>
                            {p.notes || '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {filteredPayments.length > 50 && (
                  <p className="text-xs text-gray-400 mt-2 text-center">
                    Exibindo 50 de {filteredPayments.length} registros. Exporte o PDF para ver todos.
                  </p>
                )}
              </div>
            </div>
          )}

          {filteredPayments.length === 0 && (
            <div className="bg-white rounded-lg shadow-sm p-8 text-center text-gray-500">
              <p className="text-4xl mb-3">💰</p>
              <p className="font-semibold">Nenhum pagamento registrado no período.</p>
              <p className="text-sm mt-1">Os pagamentos aparecem aqui após concluir um agendamento usando o botão "Concluído" na aba de confirmados.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsPage;
